import React, { useState, useEffect } from 'react';
import axios from 'axios';

const COLORS = {
  primary: "#0760ad",
  secondary: "#118a94",
  primaryLight: "#098a48"
};

const ServiceCardsModal = ({ isOpen, onClose, initialData }) => {
  // State management
  const [formData, setFormData] = useState({
    heading: "Our Services",
    services: []
  });
  const [loadingStates, setLoadingStates] = useState({
    isLoading: false,
    isUploading: false,
    uploadProgress: 0,
    currentUploadIndex: null
  });

  // Options for dropdowns
  const dropdownOptions = {
    colors: [
      { value: COLORS.primary, label: "Primary Blue" },
      { value: COLORS.secondary, label: "Teal" },
      { value: COLORS.primaryLight, label: "Green" }
    ],
    icons: [
      { value: "health", label: "Health" },
      { value: "heart", label: "Heart" },
      { value: "doctor", label: "Doctor" },
      { value: "lab", label: "Lab" }
    ]
  };

  // Fetch data when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setLoadingStates(prev => ({ ...prev, isLoading: true }));
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/services/service-cards/`);
          if (response.ok) {
            const data = await response.json();
            setFormData(initialData || data);
          }
        } catch (error) {
          console.error("Fetch error:", error);
        } finally {
          setLoadingStates(prev => ({ ...prev, isLoading: false }));
        }
      }
    };
    fetchData();
  }, [isOpen, initialData]);

  // Handlers
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...formData.services];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setFormData(prev => ({ ...prev, services: updatedServices }));
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append('image', file);
    form.append('category', 'service_icons');

    try {
      setLoadingStates(prev => ({ 
        ...prev, 
        isUploading: true,
        currentUploadIndex: index,
        uploadProgress: 0 
      }));

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, form, {
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.loaded * 100) / progress.total);
          setLoadingStates(prev => ({ ...prev, uploadProgress: percent }));
        }
      });

      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        handleServiceChange(index, 'iconImage', imageUrl);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Image upload failed. Please try again.");
    } finally {
      setLoadingStates(prev => ({ 
        ...prev, 
        isUploading: false,
        currentUploadIndex: null
      }));
    }
  };

  // Service card actions
  const addServiceCard = () => {
    setFormData(prev => ({
      ...prev,
      services: [
        ...prev.services,
        {
          title: "New Service",
          description: "",
          color: COLORS.primary,
          icon: "health"
        }
      ]
    }));
  };

  const removeServiceCard = (index) => {
    const updated = [...formData.services];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, services: updated }));
  };

  const submitForm = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, isLoading: true }));
      const response = await fetch(`${import.meta.env.VITE_API_URL}/services/service-cards/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onClose();
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("Failed to save changes");
    } finally {
      setLoadingStates(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Loading state
  if (loadingStates.isLoading) {
    return (
      <dialog className="modal modal-open">
        <div className="modal-box">
          <div className="flex flex-col items-center justify-center p-8">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="mt-4 text-gray-600">Loading service cards...</p>
          </div>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Edit Service Cards</h3>
          <button onClick={onClose} className="btn btn-circle btn-sm">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 space-y-6">
          {/* Section Header */}
          <div className="card bg-base-100 p-4">
            <label className="label">
              <span className="label-text font-medium">Section Title</span>
            </label>
            <input
              type="text"
              name="heading"
              value={formData.heading}
              onChange={handleTextChange}
              className="input input-bordered w-full"
              placeholder="Enter section title"
            />
          </div>

          {/* Service Cards List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Service Cards ({formData.services.length})</h4>
              <button 
                onClick={addServiceCard}
                className="btn btn-primary btn-sm"
              >
                + Add Card
              </button>
            </div>

            {formData.services.length === 0 ? (
              <div className="text-center p-8 text-gray-500 bg-base-100 rounded-lg">
                No service cards added yet
              </div>
            ) : (
              formData.services.map((service, index) => (
                <div key={index} className="card bg-base-100 p-4 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-medium">Card #{index + 1}</h4>
                    <button 
                      onClick={() => removeServiceCard(index)}
                      className="btn btn-error btn-sm btn-circle"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title Input */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Service Title</span>
                      </label>
                      <input
                        type="text"
                        value={service.title}
                        onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                        className="input input-bordered"
                        placeholder="Service name"
                      />
                    </div>

                    {/* Color Selector */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Color</span>
                      </label>
                      <select
                        value={service.color}
                        onChange={(e) => handleServiceChange(index, 'color', e.target.value)}
                        className="select select-bordered"
                      >
                        {dropdownOptions.colors.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-control mt-4">
                    <label className="label">
                      <span className="label-text">Description</span>
                    </label>
                    <textarea
                      value={service.description}
                      onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                      className="textarea textarea-bordered h-24"
                      placeholder="Service description"
                    />
                  </div>

                  {/* Icon Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Icon Type</span>
                      </label>
                      <select
                        value={service.icon}
                        onChange={(e) => handleServiceChange(index, 'icon', e.target.value)}
                        className="select select-bordered"
                      >
                        {dropdownOptions.icons.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Custom Icon</span>
                      </label>
                      <div className="flex items-center gap-2">
                        {service.iconImage && (
                          <div className="avatar">
                            <div className="w-8 rounded-full">
                              <img src={service.iconImage} alt="Custom icon" />
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, index)}
                          className="file-input file-input-bordered file-input-sm w-full"
                          disabled={loadingStates.isUploading && loadingStates.currentUploadIndex === index}
                        />
                        {loadingStates.isUploading && loadingStates.currentUploadIndex === index && (
                          <progress
                            className="progress progress-primary w-20"
                            value={loadingStates.uploadProgress}
                            max="100"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="modal-action mt-6">
          <button onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button 
            onClick={submitForm} 
            className="btn btn-primary"
            disabled={loadingStates.isLoading}
          >
            {loadingStates.isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ServiceCardsModal;